///<reference path='../../typings/index.d.ts' />


module agi.expressions.card.utils{
    /**
     * Implementation of the Object Pool pattern for PIXI Sprites.
     * @see http://gameprogrammingpatterns.com/object-pool.html
     * @requires PIXI
     * @author Dan Singer
     */
    export class SpritePool{
        private capacity:number;
        private contents:PIXI.Sprite[] = [];
        private inUseProperty:string;
        private checkIndex:number = 0;

        /**
         * Construct a new SpritePool
         * @param texture Texture or texture id which we'll use to create the contents of the pool
         * @param capacity Maximum number of sprites that can be in the pool
         * @param container Optional container which everything in the pool will be placed into. If not supplied, they won't be added to a container (Not recommended!)
         * @param inUseProperty The property that we'll use to turn a sprite "on" or "off". Defaults to "visible"
         * @param haveHitAreas Should the sprites in the pool (and the container if there is one) have nonzero hitareas? Defaults to false.
         */
        constructor(texture: string | PIXI.Texture, capacity:number, container : PIXI.Container = null, inUseProperty: string = "visible", haveHitAreas: boolean = false){
            this.capacity = capacity;
            this.inUseProperty = inUseProperty;

            let zeroRect = new PIXI.Rectangle(0,0,0,0);

            //Create the objects up-front
            for (let i=0; i<capacity; i++){
                let tex = <PIXI.Texture>(typeof texture == "string" ? PIXI.utils.TextureCache[<string>texture] : texture);
                let obj = new PIXI.Sprite(tex);
                obj[inUseProperty] = false;
                
                //Disable hitArea if necessary
                if (!haveHitAreas)
                    obj.hitArea = zeroRect; //Even though these will all point to the same rectangle, it doesn't matter since it's zero anyways. And this way we can allocate less memory too.

                //Add to container if we have one
                if (container)
                    container.addChild(obj);
                
                this.contents.push(obj);
            }
            if (!haveHitAreas && container)
                container.hitArea = zeroRect;
        }

        /**
         * Get the next available sprite 
         */
        public newSprite() : PIXI.Sprite{
            let checkedObjects = 0;

            while (checkedObjects < this.capacity){
                if (!this.contents[this.checkIndex][this.inUseProperty]){
                    this.contents[this.checkIndex][this.inUseProperty] = true;
                    let spr = this.contents[this.checkIndex];
                    this.wrapCheckIndex(); //Make sure we're ready to go for the next getter
                    return spr;                    
                }
                this.wrapCheckIndex();
                checkedObjects++;
            }

            throw new Error("SpritePool Capacity exceeded");
        }

        /**
         * Retire (turn off) the supplied sprite
         * @param sprite Sprite to retire
         */
        public retireSprite(sprite: PIXI.Sprite) : void{
            for (let i=0; i<this.capacity; i++){
                if (this.contents[i] == sprite){
                    if (!this.contents[i][this.inUseProperty])
                        throw new Error("The sprite is already retired");
                    this.contents[i][this.inUseProperty] = false;
                    this.checkIndex = i;
                    return;
                }
            }
            throw new Error("The sprite does not exist in this pool");
        }

        /**
         * Retire all of the sprites.
         */
        public retireAllSprites(){
            for (let i=0; i<this.contents.length; i++){
                this.contents[i][this.inUseProperty] = false;
                this.checkIndex = 0;
            }
        }

        /**
         * Internal method used to increment checkIndex and loop back to 0 if necessary
         */
        private wrapCheckIndex() : void{
            this.checkIndex++;
            this.checkIndex %= this.capacity;
        }


    }
}
